import { cardFace } from "@/content/cards"
import { DEEP_DIVE_FIELDS } from "@/content/deepDive"
import { MOSCOW_COPY, PLAN_BUCKETS } from "@/content/priorities"
import { WRAP_COPY } from "@/content/wrap"
import { jobSentence } from "@/store/selectors"
import type { Job, Pain, Session } from "@/types"

/*
 * The leave-behind as Markdown.
 *
 * A pure function of the documents, so the file someone downloads and the sheet
 * on screen cannot disagree — there is no stored report, and re-running this a
 * week later against the same session produces the same text.
 *
 * Structured by job, then by bucket: a session that worked three problems reads
 * as three sections rather than one merged list, because the phases only mean
 * something relative to the problem they answer.
 */

interface ReportInput {
  session: Session
  jobs: Job[]
  pains: Pain[]
  /** Stamped by the caller — the report is derived, so it holds no time itself. */
  generatedOn: string
}

export function reportMarkdown({ session, jobs, pains, generatedOn }: ReportInput): string {
  const lines: string[] = []
  const painText = (id: string) => pains.find((pain) => pain.id === id)?.text ?? null

  lines.push(`# ${WRAP_COPY.title(session.customerName || session.name)}`)
  lines.push("", WRAP_COPY.meta(generatedOn))

  const worked = jobs.filter((job) => job.cardIds.length > 0)
  if (worked.length === 0) {
    lines.push("", WRAP_COPY.noJobs)
    return lines.join("\n")
  }

  worked.forEach((job, index) => {
    const sentence = jobSentence(job, session.config.userStoryEnabled)

    lines.push("", `## ${WRAP_COPY.jobLabel(index + 1)}`)
    if (sentence) lines.push("", `**${sentence}**`)

    const from = job.painIds.map(painText).filter((text): text is string => text !== null)
    from.forEach((text) => lines.push("", `> ${text}`))

    for (const bucket of PLAN_BUCKETS) {
      const copy = MOSCOW_COPY[bucket]
      const ids = job.cardIds.filter((id) => job.placements[id]?.bucket === bucket)

      lines.push("", `### ${copy.phase} · ${copy.when}`)
      if (ids.length === 0) {
        lines.push("", `_${WRAP_COPY.nothingHere}_`)
        continue
      }

      for (const id of ids) {
        const face = cardFace(id)
        if (!face) continue
        lines.push("", `#### ${face.name}`)
        lines.push(`_${face.statement}_`)
        if (face.scope) lines.push("", face.scope)
        const note = job.placements[id]?.note.trim()
        if (note) lines.push("", `**${WRAP_COPY.whyItBelongs}:** ${note}`)
      }
    }

    /* Only cards with something in them: an untouched card would print four
     * empty bullets and read as a gap rather than as a card nobody got to. */
    const dived = job.cardIds.filter((id) => {
      const detail = job.deepDive[id]
      return detail && DEEP_DIVE_FIELDS.some((field) => detail[field.key].trim())
    })

    if (dived.length > 0) {
      lines.push("", `### ${WRAP_COPY.supportingDetail}`)
      for (const id of dived) {
        const face = cardFace(id)
        const detail = job.deepDive[id]
        if (!face || !detail) continue
        lines.push("", `#### ${face.name}`)
        for (const field of DEEP_DIVE_FIELDS) {
          const answer = detail[field.key].trim()
          if (answer) lines.push(`- **${field.label}:** ${answer}`)
        }
      }
    }

    const parked = job.cardIds.filter((id) => job.placements[id]?.bucket === "wont")
    if (parked.length > 0) {
      lines.push("", `### ${MOSCOW_COPY.wont.phase}`)
      for (const id of parked) {
        const face = cardFace(id)
        if (face) lines.push(`- ${face.name}`)
      }
    }
  })

  return lines.join("\n")
}

/**
 * Hands the Markdown to the browser as a download.
 *
 * A blob URL rather than a data URI: the report grows with every job worked and
 * a data URI would hit the address-length limit on a long session.
 */
export function downloadMarkdown(filename: string, markdown: string): void {
  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
